import React, { useState, useEffect } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
// Ensure you ran: npm install lucide-react
import { Users, Smartphone, WifiOff, Plus, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';

// -------------------------------------------------------------------------
// COMPONENT IMPORTS
// These point to: ClientApp/src/components/FileName.js
// -------------------------------------------------------------------------
import StatsCard from '../components/StatsCard';
import UserManagementModal from '../components/UserManagementModal';

// -------------------------------------------------------------------------
// TABLE IMPORTS
// These point to: ClientApp/src/components/tables/FileName.js
// NOTE: You must move your 'tables' folder inside 'src/components/'
// -------------------------------------------------------------------------
import UserTable from "../pages/tables/UserTable";
import UnassignedDevicesTable from "../pages/tables/UnassignedDevicesTable";

// -------------------------------------------------------------------------
// SERVICE IMPORT
// Points to: ClientApp/src/ParticleService.js
// -------------------------------------------------------------------------
import ParticleService from '../ParticleService';

export default function RenderAdmin() {
    const [currentUser, setUser] = useState('');
    const [isAdmin, setIsAdmin] = useState(false);
    const [showUserModal, setShowUserModal] = useState(false);
    const [syncingRegistry, setSyncingRegistry] = useState(false);
    
    // Stats State
    const [stats, setStats] = useState({ users: 0, devices: 0, unassigned: 0 });
    
    useEffect(() => {
        ParticleService.getSession().then(session => {
            if (!session.authenticated) return;
            setUser(session.email);
            setIsAdmin(session.isAdmin);
            if (session.isAdmin) fetchStats();
        }).catch(error => console.error('Could not load local session.', error));

        const refreshHandler = () => fetchStats();
        document.addEventListener('dataRefresh', refreshHandler);
        return () => document.removeEventListener('dataRefresh', refreshHandler);
    }, []);

    const fetchStats = async () => {
        try {
            const [lte, wifi, devices] = await Promise.all([
                // Add fallbacks in case service methods fail or return null
                ParticleService.getLTEUsers().catch(() => []),
                ParticleService.getWIFIUsers().catch(() => []),
                ParticleService.getProductDevices().catch(() => [])
            ]);

            const userCount = (lte ? lte.length : 0) + (wifi ? wifi.length : 0);
            const unassignedCount = devices ? devices.filter(d => !d.owner).length : 0;

            setStats({
                users: userCount,
                devices: devices ? devices.length : 0,
                unassigned: unassignedCount
            });
        } catch (error) {
            console.error("Failed to load dashboard stats", error);
        }
    };

    const synchronizeRegistry = async () => {
        setSyncingRegistry(true);
        const result = await ParticleService.synchronizeCustomerRegistry();
        if (result.error) toast.error(result.detail || result.error);
        else toast.success(`Customer registry refreshed from Particle: ${result.customersSeen} customers and ${result.devicesSeen} devices.`);
        setSyncingRegistry(false);
    };

    if (!currentUser || !isAdmin) return null;

    return (
        <div className="container-fluid p-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="text-gray-800 mb-1">Account &amp; Device Setup</h2>
                    <div className="text-muted small">Create customer accounts, claim devices, and prepare assignments</div>
                </div>
                <div className="text-muted small">Logged in as: <strong>{currentUser}</strong></div>
            </div>

            {/* 1. Hero Stats Row */}
            <Row>
                <Col md={4}>
                    <StatsCard 
                        title="Particle Accounts"
                        value={stats.users} 
                        icon={<Users size={24} />} 
                        color="#4e73df"
                    />
                </Col>
                <Col md={4}>
                    <StatsCard 
                        title="Total Devices" 
                        value={stats.devices} 
                        icon={<Smartphone size={24} />} 
                        color="#1cc88a"
                    />
                </Col>
                <Col md={4}>
                    <StatsCard 
                        title="Inventory (Unassigned)" 
                        value={stats.unassigned} 
                        icon={<WifiOff size={24} />} 
                        color="#f6c23e"
                    />
                </Col>
            </Row>

            {/* 2. Main Content Area */}
            <Row>
                {/* Left Column: User Management */}
                <Col lg={8} className="mb-4">
                    <Card className="shadow-sm h-100">
                        <Card.Header className="py-3 bg-white d-flex flex-row align-items-center justify-content-between">
                            <div>
                                <h5 className="m-0 font-weight-bold text-primary">Particle Provisioning Accounts</h5>
                                <div className="small text-muted">For account creation, device claiming, and fulfillment only</div>
                            </div>
                            <Button variant="primary" size="sm" onClick={() => setShowUserModal(true)}>
                                <Plus size={16} className="me-1" /> Add Particle Account
                            </Button>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="p-2">
                                <UserTable />
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Right Column: Tools & Inventory */}
                <Col lg={4} className="mb-4">

                    <Card className="shadow-sm mb-4">
                        <Card.Header className="py-3 bg-white">
                            <h6 className="m-0 font-weight-bold text-secondary">Registry Maintenance</h6>
                        </Card.Header>
                        <Card.Body>
                            <p className="small text-muted">Refresh current Particle ownership before fulfillment or reassignment work.</p>
                            <Button variant="outline-secondary" className="w-100 mb-2" onClick={synchronizeRegistry} disabled={syncingRegistry}>
                                <RefreshCw size={16} className={syncingRegistry ? 'me-2 spin' : 'me-2'} />
                                {syncingRegistry ? 'Refreshing…' : 'Refresh Particle Ownership'}
                            </Button>
                            <Button as={Link} to="/admin/sales" variant="outline-primary" className="w-100">Open Sales Workspace</Button>
                            <Button as={Link} to="/admin/fulfillment" variant="outline-primary" className="w-100 mt-2">Open Fulfillment Queue</Button>
                            <Button as={Link} to="/admin/payments" variant="outline-primary" className="w-100 mt-2">Open Payment Operations</Button>
                        </Card.Body>
                    </Card>
                    
                    {/* Unassigned Devices List */}
                    <Card className="shadow-sm">
                        <Card.Header className="py-3 bg-white">
                            <h6 className="m-0 font-weight-bold text-warning">Inventory</h6>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <UnassignedDevicesTable />
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Modals */}
            <UserManagementModal 
                show={showUserModal} 
                onHide={() => setShowUserModal(false)} 
            />
        </div>
    );
}
